'use client';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  CircularProgress,
  alpha,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalShipping as ShippingIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useTranslation } from '@/kit/useTranslation';

const COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
  success: '#10b981',
};

const CLUSTER_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function BulkOrderConfirmDialog({
  open,
  onClose,
  onConfirm,
  bulkOrder,
  items,
  primaryPickup,
  loading,
}) {
  const { t } = useTranslation();
  if (!bulkOrder) return null;

  const subtotal = Number(bulkOrder.subtotalAmount || 0);
  const discount = Number(bulkOrder.discountAmount || 0);
  const total = Number(bulkOrder.totalAmount || 0);
  const afterDiscount = subtotal - discount;
  const gstAmount = total > afterDiscount ? total - afterDiscount : 0;
  const routeSavings = Number(bulkOrder.routeSavingsPercent || 0);
  const hasMultiStop = items.some((it) => it.isMultiStop);
  // Compute ₹ saved from clustering: subtotal × savings% (approximate amount saved vs individual pricing)
  const rupeesSaved =
    routeSavings > 0 && subtotal > 0 ? Math.round(subtotal * (routeSavings / 100)) : 0;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CheckIcon sx={{ color: '#fff', fontSize: 28 }} />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>
              Confirm Order
            </Typography>
            <Typography sx={{ color: alpha('#fff', 0.85), fontSize: '0.75rem' }}>
              {bulkOrder.bulkOrderReference}
            </Typography>
          </Box>
        </Stack>
        {!loading && (
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Pickup Info */}
        <Box sx={{ px: 3, py: 2, bgcolor: alpha(COLORS.success, 0.05) }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocationIcon sx={{ color: COLORS.success, fontSize: 18 }} />
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>
                PICKUP
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: COLORS.primary }}>
                {primaryPickup?.address || bulkOrder.primaryPickupAddress || '—'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {/* Delivery Items */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#94a3b8',
              mb: 1.5,
              letterSpacing: 0.5,
            }}
          >
            DELIVERIES ({items.length})
          </Typography>
          <Stack spacing={1.5} sx={{ maxHeight: 240, overflowY: 'auto' }}>
            {items.map((item, idx) => {
              const addr =
                typeof item.deliveryAddress === 'object'
                  ? item.deliveryAddress?.address || item.deliveryAddress?.formatted_address
                  : item.deliveryAddress;
              return (
                <Box
                  key={item.itemId || idx}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Chip
                          label={`#${idx + 1}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: alpha(COLORS.accent, 0.15),
                            color: '#92400e',
                          }}
                        />
                        {item.clusterId != null &&
                          (() => {
                            const clusterColor =
                              CLUSTER_COLORS[(item.clusterId - 1) % CLUSTER_COLORS.length];
                            return (
                              <Chip
                                label={
                                  item.isMultiStop
                                    ? `Route ${item.clusterId}`
                                    : `Route ${item.clusterId} · solo`
                                }
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                  bgcolor: alpha(clusterColor, 0.12),
                                  color: clusterColor,
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            );
                          })()}
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PersonIcon sx={{ fontSize: 14, color: '#64748b' }} />
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {item.recipientName || '—'}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#64748b',
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 280,
                        }}
                      >
                        {addr || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 1 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                        ₹{Number(item.itemPrice || 0).toFixed(2)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                        {Number(item.distanceKm || 0).toFixed(1)} km
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Divider />

        {/* Price Breakdown */}
        <Box sx={{ px: 3, py: 2, bgcolor: '#fafafa' }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>Subtotal</Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                ₹{subtotal.toFixed(2)}
              </Typography>
            </Stack>
            {discount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: '0.85rem', color: COLORS.success }}>
                  Discount ({Number(bulkOrder.discountPercent || 0)}%)
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: COLORS.success }}>
                  −₹{discount.toFixed(2)}
                </Typography>
              </Stack>
            )}
            {gstAmount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>GST (18%)</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  ₹{gstAmount.toFixed(2)}
                </Typography>
              </Stack>
            )}
            {hasMultiStop && routeSavings > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: alpha('#10b981', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#10b981', 0.2),
                }}
              >
                <RouteIcon sx={{ fontSize: 16, color: '#10b981' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 700 }}>
                    🎉 Route clustering saved you{rupeesSaved > 0 ? ` ~₹${rupeesSaved}` : ''} (~
                    {Math.round(routeSavings)}% on distance)
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: '#64748b', mt: 0.25 }}>
                    Nearby deliveries grouped into shared routes — one rider, split cost
                  </Typography>
                </Box>
              </Box>
            )}
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Total Payable</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: COLORS.success }}>
                ₹{total.toFixed(2)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e2e8f0' }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            color: '#64748b',
            fontWeight: 600,
          }}
        >
          Go Back & Edit
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <ShippingIcon />
          }
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: COLORS.accent,
            px: 4,
            py: 1.25,
            '&:hover': { bgcolor: '#d97706' },
          }}
        >
          {loading ? 'Submitting...' : 'Confirm & Proceed to Pay'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
